'use client';

import { useResponsiveQuery } from 'atomic-layout';
import { useRouter } from 'next/router';
import React, { useContext, useEffect, useState } from 'react';
import { useSetRecoilState } from 'recoil';

import { chatIsOpenState } from '@/components/Widget/atoms';
import { AmplitudeContext } from '@/contexts/amplitude';
import { useInbound } from '@/contexts/inbound';
import { isMouseInTopArea } from '@/helpers/handleMouseMove';
import { saveInboundLeads } from '@/services/saveInboundLeads';
import { sendInboundValues } from '@/services/sendInboundValues';
import { validateIsMGM } from '@/services/validateMgmRendaExtra';
import useStore from '@/store';
import { TChannelForm } from '@/types/inbound';
import getUtmUrl from '@/utils/getUtmUrl';
import { bffHttp, useHttpClient } from '@/utils/http-client';
import { formatOnlyNumbers } from '@/utils/masks';
import { translateTpvOption } from '@/utils/translateTpvOption';

import { useMgm } from '../../hooks/useMgm';
import { useRex } from '../../hooks/useRex';
import InboundForm, { TInboundFormData } from './components/InboundFormV2';
import FinishModal from './components/InboundFormV2/components/FinishModal';
import { formData as TFormData, handleInboundFormSubmit } from './services';

type Props = {
  pathname: string;
  variantTestModal?: string;
};

const Inbound: React.FC<Props> = ({ pathname }) => {
  const { query, push } = useRouter();
  const [formData, setFormData] = useState<TFormData>({} as TFormData);
  const [showFinishModal, setShowFinishModal] = useState<boolean>(false);
  const { channels, attendanceStatus } = useInbound();
  const [defaultChannelSelected, setDefaultChannelSelected] =
    useState<TChannelForm | null>(null);
  const { analytics } = useContext(AmplitudeContext);
  const setChatIsOpen = useSetRecoilState(chatIsOpenState);

  useEffect(() => {
    analytics?.trackPageViewed();
  }, [analytics]);

  //TODO: Verificar se há uma maneira melhor de chamar o "refresh" do phonetrack em cada página
  useEffect(() => {
    setTimeout(() => window.phonetrack?.refresh?.(), 1000);
  }, []);

  useEffect(() => {
    setDefaultChannelSelected(
      attendanceStatus === 'scheduled' ? 'whatsapp' : 'chat',
    );
  }, [attendanceStatus]);

  const [isMgm] = useMgm();
  const isRex = useRex();
  const data = useStore();
  const hasMGMProps = validateIsMGM(data);

  const validationIsREXOrMGM = isMgm || isRex || hasMGMProps;

  const httpClient = useHttpClient(bffHttp);

  const initialSubmitInboundForm = async (event: TInboundFormData) => {
    event.isSmbPage = pathname.includes('/adquira-smb/');

    if (attendanceStatus === 'scheduled') {
      event.leadSourceGroup = 'adquira_madrugada';
    }

    if (query.hasOwnProperty('plan')) {
      event.isPlanPage = true;
    }

    const store = data as unknown as Record<string, string>;

    const Alltms = getUtmUrl() as Record<string, string>;

    if (Alltms?.hasOwnProperty('coupon')) {
      event.cupom = Alltms.coupon || '';
    }

    if (validationIsREXOrMGM) {
      sendInboundValues(httpClient.fetch, {
        document: event.document,
        full_name: event.name,
        phone: formatOnlyNumbers(event.phone),
        email: event.email,
        tpv: translateTpvOption(event.tpv),
        tracking: {
          coupon: Alltms.coupon || store.coupon,
          utm_medium: Alltms.utm_medium || store.utm_medium,
          utm_source: Alltms.utm_source || store.utm_source,
          utm_campaign: Alltms.utm_campaign || store.utm_campaign,
          utm_content: Alltms.utm_content,
          utm_term: Alltms.utm_term || store.utm_term,
          utm_placement: Alltms.utm_placement,
          fbid: Alltms.fbid,
          gcid: Alltms.gcid,
          client_id: Alltms.client_id,
          user_id: Alltms.user_id,
        },
      });
    }

    if (event.channel === 'chat') {
      setChatIsOpen(true);
    }
    try {
      await saveInboundLeads({
        name: event.name,
        email: event.email,
        phone: event.phone,
        document: event.document,
        tpv: event.tpv,
      });
    } catch (error) {
      console.info(error);
    }

    handleInboundFormSubmit(event);
    setFormData(event);
    setShowFinishModal(true);
  };

  const [showModal, setShowModal] = useState(false);
  const isMobile = useResponsiveQuery({ to: 'sm' });
  const checkIfModalShown = () => localStorage.getItem('meetPlansModal');
  useEffect(() => {
    const alreadyShowed = checkIfModalShown();
    if (!alreadyShowed && isMobile) {
      const timer = setTimeout(() => {
        setShowModal(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isMobile]);

  useEffect(() => {
    const alreadyShowed = checkIfModalShown();
    const handleMouseMove = (e: MouseEvent) => {
      const { clientX, clientY } = e;

      if (!alreadyShowed && isMouseInTopArea(clientX, clientY)) {
        setShowModal(true);
      }
    };

    document.addEventListener('mousemove', handleMouseMove);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [showModal]);

  return (
    <>
      <FinishModal
        channel={
          channels.length === 1
            ? channels[0]
            : (formData.channel as TChannelForm)
        }
        show={showFinishModal}
        onBackToSite={() => {
          setFormData({} as TFormData);
          setShowFinishModal(false);
          if (validationIsREXOrMGM) {
            push({ pathname: '/planos-mgm/', query });
          } else {
            push('/');
          }
        }}
      />

      <InboundForm
        channels={channels}
        defaultChannel={
          (query?.defaultChannel as TChannelForm) ?? defaultChannelSelected
        }
        onSubmit={event => {
          return initialSubmitInboundForm(event);
        }}
        pathname={pathname}
      />
    </>
  );
};

export default Inbound;
